import {
  LP_PAPABEAR_LABEL_DEFAULT_CONTENT,
  LP_PAPABEAR_LABEL_SECTION_KEYS,
  isLpPapabearLabelContentUsable,
  mergeLpPapabearLabelSection,
  normalizeLpPapabearLabelContent,
  type LpPapabearLabelContent,
  type LpPapabearLabelSectionKey,
} from "./lp-express-papabear-label";

export const LP_PAPABEAR_LABEL_LONG_WAIT_NOTICE_MS = 6 * 60_000;

export type LpPapabearLabelSectionCheckpoint = {
  status: "running" | "completed" | "fallback";
  content?: unknown;
  workflowTags?: string[];
  error?: string;
  updatedAt: string;
};

export type LpPapabearLabelGenerationCheckpoint = {
  version: 1;
  feedback?: string;
  sections: Partial<
    Record<LpPapabearLabelSectionKey, LpPapabearLabelSectionCheckpoint>
  >;
};

export function normalizeLpPapabearLabelGenerationCheckpoint(
  value: unknown,
): LpPapabearLabelGenerationCheckpoint {
  const raw = value && typeof value === "object" ? (value as any) : {};
  return {
    version: 1,
    feedback: typeof raw.feedback === "string" ? raw.feedback : undefined,
    sections:
      raw.sections && typeof raw.sections === "object" ? raw.sections : {},
  };
}

export function isTerminalLpPapabearLabelSectionCheckpoint(
  value: unknown,
): value is LpPapabearLabelSectionCheckpoint {
  return (
    !!value &&
    typeof value === "object" &&
    ((value as any).status === "completed" ||
      (value as any).status === "fallback") &&
    (value as any).content !== undefined
  );
}

export function pendingLpPapabearLabelSectionKeys(
  value: unknown,
): LpPapabearLabelSectionKey[] {
  const checkpoint = normalizeLpPapabearLabelGenerationCheckpoint(value);
  return LP_PAPABEAR_LABEL_SECTION_KEYS.filter(
    (key) =>
      !isTerminalLpPapabearLabelSectionCheckpoint(checkpoint.sections[key]),
  );
}

export function assembleLpPapabearLabelFromCheckpoint(
  value: unknown,
): LpPapabearLabelContent | null {
  const checkpoint = normalizeLpPapabearLabelGenerationCheckpoint(value);
  let content = normalizeLpPapabearLabelContent(
    LP_PAPABEAR_LABEL_DEFAULT_CONTENT,
  );
  for (const key of LP_PAPABEAR_LABEL_SECTION_KEYS) {
    const section = checkpoint.sections[key];
    if (!isTerminalLpPapabearLabelSectionCheckpoint(section)) return null;
    content = mergeLpPapabearLabelSection(
      content,
      key,
      section.content as any,
    );
  }
  return content;
}

export type LpPapabearLabelRunSnapshot = {
  status?: string | null;
  errorMessage?: string | null;
  generatedContent?: unknown;
};

export type LpPapabearLabelPollState = {
  belongsToTemplate: boolean;
  generating: boolean;
  error: string | null;
  content: LpPapabearLabelContent | null;
};

export type LpPapabearLabelReconciliationAction =
  | "complete"
  | "fail"
  | "resume"
  | null;

export function resolveLpPapabearLabelReconciliationAction(input: {
  pageStatus: string;
  runStatus: string;
  hasUsableContent: boolean;
}): LpPapabearLabelReconciliationAction {
  const { pageStatus, runStatus, hasUsableContent } = input;
  if (pageStatus === "ready" && runStatus === "completed") {
    return hasUsableContent ? null : "fail";
  }
  if (
    (pageStatus === "failed" && runStatus === "failed") ||
    (pageStatus === "cancelled" && runStatus === "cancelled")
  ) return null;
  if (pageStatus === "cancelled" || runStatus === "cancelled") return null;
  if (pageStatus === "failed" || runStatus === "failed") return "fail";
  if (hasUsableContent) return "complete";
  if (runStatus === "completed") return "fail";
  return "resume";
}

/**
 * One state resolver for initial hydration and every later poll. It has no
 * elapsed-time terminal branch: a long-running server job remains pollable
 * after the UI's six-minute courtesy notice.
 */
export function resolveLpPapabearLabelPollState(
  snapshot: LpPapabearLabelRunSnapshot,
): LpPapabearLabelPollState {
  const gc =
    snapshot.generatedContent &&
    typeof snapshot.generatedContent === "object"
      ? (snapshot.generatedContent as any)
      : {};
  if (gc.templateType !== "papabear-label") {
    return {
      belongsToTemplate: false,
      generating: false,
      error: null,
      content: null,
    };
  }
  const normalized = gc.papabearLabel
    ? normalizeLpPapabearLabelContent(gc.papabearLabel)
    : null;
  const content =
    normalized && isLpPapabearLabelContentUsable(normalized)
      ? normalized
      : null;
  if (snapshot.status === "failed") {
    return {
      belongsToTemplate: true,
      generating: false,
      error:
        snapshot.errorMessage ||
        "Papabear page generation failed. Please try again.",
      content: null,
    };
  }
  if (content) {
    return {
      belongsToTemplate: true,
      generating: false,
      error: null,
      content,
    };
  }
  return {
    belongsToTemplate: true,
    generating: true,
    error: null,
    content: null,
  };
}